// First-run (ADR-0047): a signed-in user without a workspace registers their COMPANY — agency or direct
// client, legal and tax data, commercial contact. The registration creates their own organization once
// (idempotent) and leaves the company PENDING_REVIEW: they can work right away, with no credit and no
// commercial authorization until Media Interactiva validates it. Invited users never see this form.
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { api, call, csrf } from "@/api/client";
import { keys, useMe, useMemberships } from "@/api/hooks";
import { Button } from "@/ui/button";
import { Callout } from "@/ui/layout";
import { CompanyForm, EMPTY_COMPANY, type CompanyValues } from "../commercial/company-form";

export function OnboardingPage() {
  const { data: me, isLoading } = useMe();
  const { data: memberships } = useMemberships(Boolean(me));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  if (!isLoading && !me) return <Navigate to="/login" search={{ next: "/onboarding" }} />;

  const register = async (values: CompanyValues) => {
    const r = await call(() => api.POST("/me/company", { body: values }));
    if (r.csrfToken) csrf.set(r.csrfToken);
    await queryClient.invalidateQueries({ queryKey: keys.memberships });
    await navigate({ to: `/o/${r.organizationId}` });
  };

  const hasWorkspaces = (memberships?.length ?? 0) > 0;
  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <div className="grid w-full max-w-xl gap-6">
        <div className="grid gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Registra tu empresa</h1>
          <p className="text-sm text-muted">
            Con estos datos creamos tu espacio de trabajo. Puedes empezar a crear de inmediato; Media Interactiva
            validará tu empresa antes de habilitar órdenes de compra o condiciones comerciales. No necesitas tarjeta.
          </p>
        </div>
        <CompanyForm
          initial={{ ...EMPTY_COMPANY, contactEmail: me?.user.email ?? "", contactName: me?.user.displayName ?? "" }}
          submitLabel="Registrar empresa"
          onSubmit={register}
        />
        <Callout title="¿Te invitaron a un equipo?">
          Abre el enlace de invitación que recibiste: no necesitas registrar la empresa otra vez.
        </Callout>
        {hasWorkspaces ? (
          <Button variant="link" onClick={() => void navigate({ to: "/" })}>
            Volver a mis espacios de trabajo
          </Button>
        ) : null}
      </div>
    </main>
  );
}
